import {
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { DiscoverViewContext } from "contexts/DiscoverView";
import { MetricsContext, TimeFrame } from "contexts/Metrics";
import { OrganizationUserContext } from "contexts/Organization";
import { SubscriptionContext } from "contexts/Subscription";
import { useContext, useEffect } from "react";
import { AddOnItemName, UpgradeDialogSource } from "schemas/dashboard";

import AssistantCTA from "features/Home/Sidebar/AssistantPreview/AssistantCTA";
import { useSubscription } from "utils/useSubscription";

import Metric from "./Metric";
import MetricsByTemplate from "./Template";
import { TimePeriod } from "./schema";
import styles from "./styles";

export default function MetricsHelper() {
  const { currentUser, currentOrg } = useContext(OrganizationUserContext);
  const { setDiscoverTab } = useContext(DiscoverViewContext);
  const { fetchMetrics } = useContext(MetricsContext);
  const { handleCheckout } = useSubscription();
  const { setUpgradeDialogSource } = useContext(SubscriptionContext);

  const closeDialog = () => {
    setDiscoverTab(null);
  };

  const firstName =
    currentUser && currentUser.name && currentUser.name.split(" ")[0];

  useEffect(() => {
    [TimePeriod.today, TimePeriod.thisWeek, TimePeriod.thisMonth].forEach(
      (period) => fetchMetrics(period),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box sx={styles.root}>
      <Grid container>
        {/* Top Bar */}
        <Grid item xs={12} sx={styles.header}>
          <Grid container justifyContent="space-between" alignItems="center">
            <Typography
              variant="h6"
              sx={{ fontSize: 14, textTransform: "uppercase" }}
            >
              {firstName
                ? `${firstName}'${firstName.endsWith("s") ? "" : "s"} `
                : ""}
              Metrics
            </Typography>
            <IconButton sx={styles.close} onClick={closeDialog}>
              <Box component="i" className="fa-regular fa-xmark" />
            </IconButton>
          </Grid>
        </Grid>

        {!currentOrg?.isVaUser && !currentOrg?.isAutopilotUser && (
          <Box
            sx={{
              mt: 2,
              ml: 2,
              p: 2,
              border: "1px solid",
              borderColor: "green.main",
              borderRadius: 3,
            }}
          >
            <Grid container justifyContent={"center"}>
              <Chip
                sx={{
                  backgroundColor: "yellow.main",
                  fontWeight: 600,
                  fontSize: 10,
                  mb: 2,
                }}
                label="SAVE 4 HOURS ON OUTREACH"
              />
            </Grid>
            <Typography textAlign={"center"} sx={{ fontSize: 12 }}>
              Creators that upgrade to{" "}
              {currentOrg?.showAutopilotTier
                ? "Bento Autopilot"
                : "Bento AI VA"}{" "}
              are <strong>33x more </strong>
              likely to get paid brand deals ⚡
            </Typography>

            <Button
              sx={{ mt: 2, py: 0.5 }}
              disableElevation
              variant="contained"
              size="small"
              fullWidth
              onClick={() =>
                setUpgradeDialogSource(UpgradeDialogSource.va_metrics_sidebar)
              }
            >
              Upgrade to automate your outreach
            </Button>
          </Box>
        )}

        <Metric timeFrame={TimeFrame.daily} />

        <Grid item xs={12} sx={{ px: 2, mt: 1, mb: -1 }}>
          <AssistantCTA
            title="Need help improving your metrics?"
            body="Chat with AI for personalized suggestions."
            openContext={{ promptPage: "metrics_sidebar" }}
            fullWidthButton
          />
        </Grid>

        <Metric timeFrame={TimeFrame.weekly} />

        <Metric timeFrame={TimeFrame.monthly} />

        <MetricsByTemplate />

        <Grid item xs={12} sx={styles.metricsWrapper}>
          <Typography sx={{ color: "primary.main" }}>
            <strong>WANT TO LEVEL UP YOUR METRICS? 📝 </strong>
          </Typography>

          <Typography variant="body2">
            Purchase a pitch template written by our copywriters to make you
            stand out
          </Typography>

          <Button
            size="small"
            variant="contained"
            disableElevation
            sx={{ mt: 1 }}
            onClick={() => handleCheckout(AddOnItemName.pitching_templates)}
          >
            Purchase
          </Button>
        </Grid>

        <Grid item xs={12} sx={{ px: 3 }}>
          <Typography sx={styles.title} paragraph>
            Bento Best Practice Framework
          </Typography>
          <Typography variant="body2">Send at least 30 emails/week</Typography>
          <Typography variant="body2">Set 1-2 follow-ups</Typography>
          <Typography variant="body2">
            Follow up if a contact opens the email multiple times
          </Typography>
          <Typography variant="body2">
            Respond to replies within 1 hour
          </Typography>
          <>
            <Typography sx={styles.title} paragraph>
              Bento Average Industry Metrics
              <Tooltip
                title={
                  "If your metrics differ greatly from these industry metrics, there may be an issue. Contact us at hello@onbento.com and we will help you troubleshoot."
                }
              >
                <Box
                  component="i"
                  sx={styles.infoIcon}
                  className="fa-regular fa-circle-info"
                />
              </Tooltip>
            </Typography>
            <Typography variant="body2">55.9% open rate</Typography>
            <Typography variant="body2" paragraph>
              9.4% response rate
            </Typography>
          </>
        </Grid>
      </Grid>
    </Box>
  );
}
